import { lazy } from 'react';

/**
 * Wraps a dynamic import with retry logic to handle ChunkLoadError (e.g. after
 * deployment when the browser requests old chunk filenames, or transient network failures).
 *
 * @param {() => Promise<{ default: React.ComponentType }>} importFn - Dynamic import () => import('./path')
 * @param {number} retries - Number of retry attempts (default 3)
 * @param {number} intervalMs - Delay between retries in ms (default 1500)
 * @returns {React.LazyExoticComponent} Lazy component that retries on chunk load failure
 */
export function lazyRetry(importFn, retries = 3, intervalMs = 1500) {
  const isChunkLoadError = (err) =>
    err?.name === 'ChunkLoadError' ||
    (err?.message && typeof err.message === 'string' && err.message.includes('Loading chunk'));

  const loadWithRetry = () =>
    new Promise((resolve, reject) => {
      const tryLoad = (attempt) => {
        importFn()
          .then(resolve)
          .catch((err) => {
            const shouldRetry =
              (isChunkLoadError(err) || err?.message === 'Loading script failed') &&
              attempt < retries;
            if (shouldRetry) {
              setTimeout(() => tryLoad(attempt + 1), intervalMs);
            } else {
              reject(err);
            }
          });
      };
      tryLoad(0);
    });

  return lazy(loadWithRetry);
}
